// Global browser APIs for Gatsby
// Import global stylesheet(s) here so Gatsby includes them in the build.
import "./src/styles/global.scss";

import React from 'react';
import { Layout } from './src/components/Layout';

// Delay (ms) before scrolling to an anchor target.
const ANCHOR_SCROLL_DELAY = 1000;

// Google Tag Manager bootstrap — loaded after first render so tags that
// bind to page DOM elements can find them.
const loadGTM = () => {
	if (typeof window === 'undefined' || window.__gtmLoaded) return;
	window.__gtmLoaded = true;
	(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
	new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
	j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
	'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
	})(window,document,'script','dataLayer','GTM-M887LXK');
};

export const wrapPageElement = ({ element, props }) => {
	return React.createElement(Layout, { element, ...props });
};

// Scroll to a hash target, accounting for the sticky header.
const scrollToElement = (hash) => {
	const el = document.querySelector(hash);
	if (el) {
		el.scrollIntoView({ behavior: 'smooth' });
	}
};

// Intercept same-page hash link clicks and delay the scroll by 1s.
export const onInitialClientRender = () => {
	loadGTM();

	// Mark body as keyboard-user on Tab; clear on mouse/touch/pointer input.
	// Enables focus-visible styling scoped to `.keyboard-user`.
	// Also traps Tab focus inside the OneTrust cookie banner (if present) until
	// the user makes a consent choice and the banner is removed from the DOM.
	const FOCUSABLE_SELECTOR = [
		'a[href]',
		'area[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(',');

	// `position: fixed` elements return `offsetParent === null`, so use
	// `getClientRects().length` — non-zero means the element is rendered.
	const isVisible = (el) => !!el && el.getClientRects().length > 0;

	const getBannerFocusables = () => {
		const banner = document.querySelector('#onetrust-banner-sdk');
		if (!banner || banner.getAttribute('aria-hidden') === 'true' || !isVisible(banner)) {
			return null;
		}
		const focusables = Array.from(banner.querySelectorAll(FOCUSABLE_SELECTOR))
			.filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && isVisible(el));
		return focusables.length ? { banner, focusables } : null;
	};

	const addKeyboardUser = (e) => {
		if (e.key !== 'Tab') return;
		document.body.classList.add('keyboard-user');

		const trap = getBannerFocusables();
		if (!trap) return;

		const { banner, focusables } = trap;
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const active = document.activeElement;
		const insideBanner = banner.contains(active);

		if (!insideBanner) {
			e.preventDefault();
			first.focus();
			return;
		}
		if (e.shiftKey && active === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && active === last) {
			e.preventDefault();
			first.focus();
		}
	};
	const removeKeyboardUser = () => document.body.classList.remove('keyboard-user');
	document.addEventListener('keydown', addKeyboardUser);
	document.addEventListener('mousedown', removeKeyboardUser);
	document.addEventListener('pointerdown', removeKeyboardUser);
	document.addEventListener('touchstart', removeKeyboardUser, { passive: true });

	// When the OneTrust consent banner is removed or hidden (user made a choice),
	// move focus so the next Tab press lands on the "Skip to main content" link.
	// For keyboard users we focus the skip link directly; for mouse users we
	// focus <body> instead to avoid visually revealing the link.
	const focusSkipLink = () => {
		const skipLink = document.querySelector('.skip-to-main');
		if (!skipLink) return;
		const keyboardUser = document.body.classList.contains('keyboard-user');
		if (keyboardUser) {
			try {
				skipLink.focus({ preventScroll: true });
			} catch {
				skipLink.focus();
			}
			return;
		}
		if (!document.body.hasAttribute('tabindex')) {
			document.body.setAttribute('tabindex', '-1');
		}
		try {
			document.body.focus({ preventScroll: true });
		} catch {
			document.body.focus();
		}
	};
	const isBannerVisible = () => {
		const banner = document.querySelector('#onetrust-banner-sdk');
		return !!(banner && banner.getAttribute('aria-hidden') !== 'true' && isVisible(banner));
	};
	let bannerWasVisible = false;
	const consentObserver = new MutationObserver(() => {
		const visible = isBannerVisible();
		if (bannerWasVisible && !visible) {
			focusSkipLink();
			consentObserver.disconnect();
			return;
		}
		bannerWasVisible = visible;
	});
	consentObserver.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['aria-hidden', 'style', 'class'],
	});

	document.addEventListener('click', (e) => {
		const anchor = e.target.closest('a[href]');
		if (!anchor) return;
		// Ignore modified clicks and links opening in a new tab/window.
		if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
		const target = anchor.getAttribute('target');
		if (target && target !== '_self') return;

		const href = anchor.getAttribute('href') || '';
		if (!href.includes('#')) return;

		// Same-page hash link (e.g. "#foo" or current-path "#foo")
		const url = new URL(anchor.href, window.location.href);
		const samePage =
			url.origin === window.location.origin &&
			url.pathname === window.location.pathname;
		if (!samePage || !url.hash) return;

		e.preventDefault();
		// Update the URL so the hash is preserved without triggering native jump.
		window.history.pushState(null, '', url.hash);

		if (anchor.classList.contains('skip-to-main')) {
			// Skip link: focus main and scroll immediately, synchronously in the user gesture.
			const target = document.querySelector(url.hash);
			if (target) {
				if (!target.hasAttribute('tabindex')) {
					target.setAttribute('tabindex', '-1');
				}
				try {
					target.focus({ preventScroll: true });
				} catch {
					target.focus();
				}
				// Bypass the global `scroll-padding-top` so the skip link jumps to
				// the exact top of `#main-content`.
				const top = target.getBoundingClientRect().top + window.scrollY;
				window.scrollTo({ top, behavior: 'smooth' });
			}
			return;
		}

		setTimeout(() => scrollToElement(url.hash), ANCHOR_SCROLL_DELAY);
	});
};

export const onRouteUpdate = ({ location }) => {
	if (location.hash) {
		// Cross-page navigation with hash — wait for the page to render,
		// then scroll. CSS scroll-padding-top handles the header offset.
		setTimeout(() => scrollToElement(location.hash), ANCHOR_SCROLL_DELAY);
	} else {
		// Scroll to top instantly for non-anchor navigations
		window.scrollTo({ top: 0, behavior: 'instant' });
	}
	// Reset keyboard focus to the top of the document on client-side navigation
	// so the next Tab press lands on the "Skip to main content" link instead of
	// continuing from wherever the previously activated link was.
	// Skip focus reset when navigating to a hash, but still run hash scrolling below.
	if (!location.hash) {
		const skipLink = document.querySelector(".skip-to-main")
		if (skipLink) {
			// Defer until after @reach/router (used by Gatsby) auto-focuses the route
			// wrapper, otherwise our focus reset is immediately overridden.
			const resetFocusToTop = () => {
				const active = document.activeElement
				if (active && active !== document.body && typeof active.blur === "function") {
					active.blur()
				}
				if (!document.body.hasAttribute("tabindex")) {
					document.body.setAttribute("tabindex", "-1")
				}
				try {
					document.body.focus({ preventScroll: true })
				} catch {
					document.body.focus()
				}
			}

			if (typeof window.requestAnimationFrame === "function") {
				// Two RAFs ensures we run after reach-router's post-render focus call.
				window.requestAnimationFrame(() => {
					window.requestAnimationFrame(resetFocusToTop)
				})
			} else {
				window.setTimeout(resetFocusToTop, 0)
			}
		}
	}
};

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
	if (location.hash) return false;
	return [0, 0];
};